import React, { useEffect, useState } from 'react';
import ManhuaCard from './ManhuaCard';
import { getManhuaBrowse } from '../services/api';

/**
 * Manhua discovery homepage — horizontally-scrolling rails (Popular, Latest
 * Updates, genres, shounen/seinen demographics). Shown on /manhua when there's
 * no active search; searching swaps this out for ManhuaResults.
 *
 * Only English-readable titles come back from /manhua/browse, so everything
 * here is something a reader can actually open.
 */
const RailSkeleton = () => (
  <div className="mb-9">
    <div className="h-5 w-40 bg-zinc-800/60 rounded mb-3 animate-pulse" />
    <div className="flex gap-4 overflow-hidden">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="w-[150px] shrink-0">
          <div className="aspect-[2/3] bg-zinc-800/60 rounded-lg animate-pulse" />
        </div>
      ))}
    </div>
  </div>
);

const Rail = ({ title, items, onCardClick }) => (
  <section className="mb-9">
    <h3 className="text-lg font-semibold text-zinc-100 mb-3 px-0.5">{title}</h3>
    <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 [scrollbar-width:thin] snap-x">
      {items.map((m) => (
        <div key={m.id} className="w-[150px] sm:w-[160px] shrink-0 snap-start">
          <ManhuaCard manhua={m} onClick={onCardClick} />
        </div>
      ))}
    </div>
  </section>
);

const ManhuaHome = ({ onCardClick, stats }) => {
  const [rails, setRails] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getManhuaBrowse()
      .then((d) => { if (!cancelled) setRails(d.rails || []); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    // Fail soft: the search bar above still works; just no rails.
    return (
      <div className="text-center py-16 text-zinc-500">
        <div className="text-5xl mb-3">📖</div>
        <p>Search {stats?.total_manhua ? stats.total_manhua.toLocaleString() : 'thousands of'} titles above to get started.</p>
      </div>
    );
  }

  if (!rails) {
    return (
      <div className="animate-fade-in">
        {Array.from({ length: 4 }).map((_, i) => <RailSkeleton key={i} />)}
      </div>
    );
  }

  if (rails.length === 0) {
    return (
      <div className="text-center py-16 text-zinc-500">
        <div className="text-5xl mb-3">📖</div>
        <p>Search the catalog above to discover webtoons, manhua and manga.</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {rails.map((r) => (
        <Rail key={r.key} title={r.title} items={r.items} onCardClick={onCardClick} />
      ))}
    </div>
  );
};

export default ManhuaHome;
